import React from 'react';
import DatePicker from 'react-datepicker';
import { endOfMonth, format, isValid, parse } from 'date-fns';
import 'react-datepicker/dist/react-datepicker.css';

/** What the API and MySQL DATE columns use. */
const STORAGE_FORMAT = 'yyyy-MM-dd';

/**
 * Formats the field accepts while typing. The first is what it displays, and
 * it is day-first because that is how dates are written here and on the rest
 * of the HRMS.
 *
 * Passing an array matters beyond convenience: given a single format,
 * react-datepicker retries a failed parse with the format truncated to the
 * typed text's length and then falls back to `new Date(text)`, so typing
 * "15-09-1990" into a yyyy-MM-dd field lands on a nonsense date. The array
 * branch tries each format properly and keeps nothing if none match.
 */
const INPUT_FORMATS = ['dd-MM-yyyy', 'dd/MM/yyyy', STORAGE_FORMAT];

/**
 * Stored string to Date. Parsed as local midnight rather than via
 * `new Date(string)`, which reads a bare yyyy-MM-dd as UTC and can land on the
 * previous day once it is rendered in local time.
 */
const toDate = (value) => {
  if (!value) return null;

  const parsed = parse(value, STORAGE_FORMAT, new Date());
  return isValid(parsed) ? parsed : null;
};

/** Today, in the storage format — the latest a date of birth can be. */
export const TODAY = format(new Date(), STORAGE_FORMAT);

/**
 * The window a joining date may fall in: from 1 January of the current year to
 * the end of the current month, so the month in hand can be planned out but a
 * later one cannot be picked.
 */
export const YEAR_START = `${new Date().getFullYear()}-01-01`;
export const MONTH_END = format(endOfMonth(new Date()), STORAGE_FORMAT);

/**
 * Punctuates digits as they are typed, so "11101994" becomes "11-10-1994"
 * without anyone reaching for the dash key. Anything that is not a digit is
 * dropped, which also lets a pasted "11/10/1994" settle into the same shape.
 */
export const maskDate = (raw) => {
  const digits = String(raw).replace(/\D/g, '').slice(0, 8);

  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}-${digits.slice(2)}`;

  return `${digits.slice(0, 2)}-${digits.slice(2, 4)}-${digits.slice(4)}`;
};

/**
 * react-datepicker reads the typed text off the event for both the displayed
 * value and the parse, so rewriting it here is what makes the mask stick.
 */
const MaskedInput = React.forwardRef((props, ref) => {
  const { onChange, onComplete, ...rest } = props;

  return (
    <input
      {...rest}
      ref={ref}
      inputMode="numeric"
      onChange={(e) => {
        const masked = maskDate(e.target.value);
        e.target.value = masked;
        onChange(e);

        // A full dd-MM-yyyy leaves the calendar nothing to add, so it goes away.
        if (masked.length === 10 && onComplete) onComplete();
      }}
    />
  );
});

/**
 * A date input matching the rest of the HRMS: typed and shown as dd-MM-yyyy,
 * stored as yyyy-MM-dd so it drops straight into the API and back out of MySQL
 * DATE columns unchanged.
 */
const DateField = ({
  value,
  onChange,
  placeholder = 'dd-mm-yyyy',
  required = false,
  minDate,
  maxDate,
  disabled = false,
}) => {
  const picker = React.useRef(null);

  // Deferred, so react-datepicker's own state update for the keystroke has
  // already run and cannot re-open the calendar behind us.
  const close = () => {
    window.setTimeout(() => {
      if (picker.current) picker.current.setOpen(false);
    }, 0);
  };

  return (
  <DatePicker
    ref={picker}
    selected={toDate(value)}
    onChange={(date) => onChange(date && isValid(date) ? format(date, STORAGE_FORMAT) : '')}
    dateFormat={INPUT_FORMATS}
    placeholderText={placeholder}
    className={`form-control${required ? ' required-input' : ''}`}
    minDate={toDate(minDate)}
    maxDate={toDate(maxDate)}
    disabled={disabled}
    showMonthDropdown
    showYearDropdown
    dropdownMode="select"
    // A date of birth is decades back, so the year list has to reach there.
    yearDropdownItemNumber={100}
    scrollableYearDropdown
    customInput={<MaskedInput onComplete={close} />}
    // Tab moves on and Enter commits, so neither should leave the calendar up.
    onKeyDown={(e) => {
      if (e.key === 'Tab' || e.key === 'Enter') close();
    }}
    autoComplete="off"
    // Keep the calendar clear of the dialog's scrolling body.
    popperProps={{ strategy: 'fixed' }}
  />
  );
};

export default DateField;

/** The month picker used by the report filter, in the same yyyy-MM shape. */
export const MonthField = ({ value, onChange }) => (
  <DatePicker
    selected={value ? toDate(`${value}-01`) : null}
    onChange={(date) => onChange(date && isValid(date) ? format(date, 'yyyy-MM') : '')}
    dateFormat="MM-yyyy"
    showMonthYearPicker
    placeholderText="Select month"
    className="form-control"
    autoComplete="off"
    popperProps={{ strategy: 'fixed' }}
  />
);
